import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Row,
  Col,
  Select,
  Spin,
  Typography,
  Tooltip,
  Space,
  Button,
  Empty,
  Badge,
  Table,
  Tag,
} from "antd";
import {
  GiftOutlined,
  ReloadOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  PercentageOutlined,
  RiseOutlined,
  TrophyOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  TeamOutlined,
  GlobalOutlined,
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined,
  PlayCircleOutlined,
  CrownOutlined,
  AimOutlined,
  FireOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  ScheduleOutlined,
  SelectOutlined,
  UnorderedListOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  getPromotionsDashboard,
  getPromotionsStats,
  getTopPerformingPromotions,
  getPromotionsPerformanceOverTime,
  getAllPromotionsPaginated,
} from "@api/subscription/promotion";

const { Text } = Typography;
const { Option } = Select;

// Card wrapper component (same as VisitorAnalytics)
const ChartCard = ({ title, subtitle, badge, children, minHeight = 300 }) => (
  <div style={{
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
  }}>
    <div style={{
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}>
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      {badge !== undefined && (
        <Badge count={badge} style={{ backgroundColor: "#8b5cf6" }} overflowCount={999} />
      )}
    </div>
    <div style={{ minHeight }}>{children}</div>
  </div>
);

// Metric card component (same as VisitorAnalytics)
const MetricCard = ({ title, value, icon, color, suffix, prefix }) => {
  const colorMap = {
    blue: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", text: "#1d4ed8", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
    green: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", text: "#059669", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
    purple: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", text: "#7c3aed", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
    orange: { bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", text: "#d97706", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
    pink: { bg: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", text: "#db2777", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
    red: { bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", text: "#dc2626", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
    teal: { bg: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)", text: "#0d9488", gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" },
  };
  const c = colorMap[color] || colorMap.blue;

  const displayValue = () => {
    const formattedValue = typeof value === "number" ? value.toLocaleString() : value;
    return `${prefix || ""}${formattedValue}${suffix || ""}`;
  };

  return (
    <div style={{
      background: c.bg,
      borderRadius: "16px",
      padding: "20px",
      border: "1px solid rgba(0,0,0,0.06)",
      minHeight: "120px",
    }}>
      <div style={{
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        background: c.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "18px",
        marginBottom: "14px",
      }}>
        {icon}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: c.text, lineHeight: 1.1 }}>
        {displayValue()}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 500, color: "#64748b", marginTop: "4px" }}>
        {title}
      </div>
    </div>
  );
};

const PromotionsDashboard = () => {
  const history = useHistory();
  const location = useLocation();

  const getInitialDateRange = () => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const validDateRanges = [7, 30, 90, 365];
    const parsedDate = parseInt(dateParam);
    return validDateRanges.includes(parsedDate) ? parsedDate : 30;
  };

  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");
    const validTabs = ["overview", "performance", "gamification", "audience", "geographic", "campaigns"];
    return validTabs.includes(tabParam) ? tabParam : "overview";
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Tab configuration
  const tabs = [
    { key: "overview", label: "Overview", icon: <TrophyOutlined /> },
    { key: "performance", label: "Performance", icon: <LineChartOutlined /> },
    { key: "gamification", label: "Gamification", icon: <PlayCircleOutlined /> },
    { key: "audience", label: "Audience", icon: <TeamOutlined /> },
    { key: "geographic", label: "Geographic", icon: <GlobalOutlined /> },
    { key: "campaigns", label: "Campaigns", icon: <BarChartOutlined /> },
  ];

  // Calculate date filters
  const getDateFilters = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  // React Query hooks
  const { data: dashboard, isLoading: dashboardLoading, refetch } = useQuery(
    ["promotionsDashboard", dateRange],
    async () => {
      const res = await getPromotionsDashboard(getDateFilters());
      return res.data?.data || res.data;
    },
    { staleTime: 60000, keepPreviousData: true }
  );

  const { data: stats } = useQuery(
    ["promotionsStats", dateRange],
    async () => {
      const res = await getPromotionsStats(getDateFilters());
      return res.data?.data || res.data;
    },
    { staleTime: 60000, keepPreviousData: true }
  );

  const { data: topPromotions = [] } = useQuery(
    ["topPromotions", dateRange],
    async () => {
      const res = await getTopPerformingPromotions(10, "conversions");
      return res.data?.data?.promotions || res.data?.promotions || [];
    },
    { enabled: activeTab === "overview" || activeTab === "campaigns", staleTime: 60000, keepPreviousData: true }
  );

  const { data: performanceData = [] } = useQuery(
    ["promotionsPerformance", dateRange],
    async () => {
      const res = await getPromotionsPerformanceOverTime({
        ...getDateFilters(),
        groupBy: "day",
      });
      return res.data?.data?.performance || res.data?.performance || [];
    },
    { enabled: activeTab === "overview" || activeTab === "performance", staleTime: 60000, keepPreviousData: true }
  );

  const { data: allPromotions } = useQuery(
    ["allPromotions", dateRange],
    async () => {
      const res = await getAllPromotionsPaginated({ perPage: 100 });
      return res.data?.data || res.data;
    },
    { enabled: activeTab === "campaigns" || activeTab === "audience", staleTime: 60000, keepPreviousData: true }
  );

  const loading = dashboardLoading;

  const updateURL = (newDateRange, newTab) => {
    const params = new URLSearchParams(location.search);
    params.set("dateRange", newDateRange.toString());
    params.set("tab", newTab);
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    updateURL(newDateRange, activeTab);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    updateURL(dateRange, newTab);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const tabParam = urlParams.get("tab");

    if (dateParam) {
      const validDateRanges = [7, 30, 90, 365];
      const parsedDate = parseInt(dateParam);
      if (validDateRanges.includes(parsedDate) && parsedDate !== dateRange) {
        setDateRange(parsedDate);
      }
    }

    if (tabParam) {
      const validTabs = ["overview", "performance", "gamification", "audience", "geographic", "campaigns"];
      if (validTabs.includes(tabParam) && tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
    }
  }, [location.search]);

  if (loading && !dashboard) {
    return (
      <div style={{
        background: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <Spin size="large" />
        <Text style={{ color: "#64748b" }}>Loading promotions analytics...</Text>
      </div>
    );
  }

  // Calculate totals
  const totals = {
    totalViews: dashboard?.totalViews || stats?.totalViews || 0,
    totalClicks: dashboard?.totalClicks || stats?.totalClicks || 0,
    totalConversions: dashboard?.totalConversions || stats?.totalConversions || 0,
    totalRevenue: dashboard?.totalRevenue || stats?.totalRevenue || 0,
    ctr: dashboard?.ctr || stats?.ctr || 0,
    conversionRate: dashboard?.conversionRate || stats?.conversionRate || 0,
    totalPromotions: dashboard?.totalPromotions || stats?.totalPromotions || 0,
    activePromotions: dashboard?.activePromotions || stats?.activePromotions || 0,
    inactivePromotions: dashboard?.inactivePromotions || stats?.inactivePromotions || 0,
    scheduledPromotions: dashboard?.scheduledPromotions || stats?.scheduledPromotions || 0,
    gamificationStats: dashboard?.gamificationStats || stats?.gamificationStats || {},
    audienceBreakdown: dashboard?.audienceBreakdown || stats?.audienceBreakdown || {},
    deviceBreakdown: dashboard?.deviceBreakdown || stats?.deviceBreakdown || {},
    typeBreakdown: dashboard?.typeBreakdown || stats?.typeBreakdown || [],
    countryBreakdown: dashboard?.countryBreakdown || stats?.countryBreakdown || [],
  };

  // Process performance data for charts
  const maxDailyViews = Math.max(...performanceData.map(d => d.views || 0), 1);
  const maxDailyClicks = Math.max(...performanceData.map(d => d.clicks || 0), 1);
  const maxDailyConversions = Math.max(...performanceData.map(d => d.conversions || 0), 1);

  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

  // Game types for gamification
  const gameTypes = [
    { key: "spin_wheel", label: "Spin Wheel", icon: <PlayCircleOutlined />, color: "#8b5cf6" },
    { key: "scratch_card", label: "Scratch Card", icon: <CrownOutlined />, color: "#f59e0b" },
    { key: "slot_machine", label: "Slot Machine", icon: <ThunderboltOutlined />, color: "#ec4899" },
    { key: "pick_a_box", label: "Pick a Box", icon: <GiftOutlined />, color: "#10b981" },
  ];

  // Device icons mapping
  const deviceIcons = {
    mobile: <MobileOutlined />,
    desktop: <LaptopOutlined />,
    tablet: <TabletOutlined />,
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "calc(100vh - 80px)", padding: "24px" }}>
      {/* Header */}
      <div style={{ maxWidth: "1400px", margin: "0 auto 24px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", marginBottom: "8px" }}>
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1e293b", margin: "0 0 4px 0" }}>
              Promotions Analytics
            </h1>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Track promotion performance, conversions, and revenue generation
            </p>
          </div>
          <Space size="small">
            <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 140 }}>
              <Option value={7}>Last 7 days</Option>
              <Option value={30}>Last 30 days</Option>
              <Option value={90}>Last 90 days</Option>
              <Option value={365}>Last year</Option>
            </Select>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => refetch()}
            />
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() => history.push("/super-admin/promotions/list")}
            >
              View Promotions
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push("/super-admin/promotions/create")}
            >
              Create Promotion
            </Button>
          </Space>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Metric Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Total Views" value={totals.totalViews} icon={<EyeOutlined />} color="blue" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Total Clicks" value={totals.totalClicks} icon={<SelectOutlined />} color="green" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Conversions" value={totals.totalConversions} icon={<AimOutlined />} color="purple" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Revenue" value={totals.totalRevenue} icon={<DollarOutlined />} color="orange" prefix="$" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="CTR" value={(totals.ctr || 0).toFixed(2)} icon={<PercentageOutlined />} color="pink" suffix="%" />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard title="Conv. Rate" value={(totals.conversionRate || 0).toFixed(2)} icon={<RiseOutlined />} color="red" suffix="%" />
          </Col>
        </Row>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                padding: "10px 20px",
                fontSize: "13px",
                fontWeight: activeTab === tab.key ? 600 : 500,
                color: activeTab === tab.key ? "#fff" : "#64748b",
                background: activeTab === tab.key
                  ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                  : "#fff",
                border: activeTab === tab.key ? "none" : "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: activeTab === tab.key ? "0 4px 12px rgba(139, 92, 246, 0.3)" : "none",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <>
            {/* Charts Row 1 */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={8}>
                <ChartCard title="Top Promotions" subtitle="By conversions" badge={topPromotions?.length} minHeight={320}>
                  {topPromotions?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {topPromotions.slice(0, 8).map((promo, index) => {
                        const maxConversions = Math.max(...topPromotions.map(p => p.conversions || 0), 1);
                        const widthPercent = ((promo.conversions || 0) / maxConversions) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={promo._id || promo.id || index} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 12px",
                            background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #c4b5fd" : "1px solid transparent",
                            borderRadius: "10px",
                          }}>
                            <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "11px", fontWeight: 700 }}>{index + 1}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                <Text style={{ fontWeight: index === 0 ? 700 : 500, fontSize: "13px" }} ellipsis>
                                  {index === 0 && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                                  {promo.name || promo.title}
                                </Text>
                                <Text strong style={{ color }}>{(promo.conversions || 0).toLocaleString()}</Text>
                              </div>
                              <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                                <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "3px" }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No promotions data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="Daily Performance" subtitle="Views, clicks & conversions" minHeight={320}>
                  {performanceData.length > 0 ? (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {performanceData.slice(-7).map((day, index) => {
                          const widthPercent = ((day.views || 0) / maxDailyViews) * 100;
                          const date = new Date(day.date);
                          const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
                          const isBest = day.views === Math.max(...performanceData.slice(-7).map(d => d.views || 0));
                          const color = isBest ? "#10b981" : "#3b82f6";
                          return (
                            <div key={day.date} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{ width: "36px", fontSize: "12px", fontWeight: isBest ? 700 : 500, color }}>{dayName}</div>
                              <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                                <div style={{ width: `${Math.max(widthPercent, 3)}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: widthPercent > 20 ? "8px" : 0 }}>
                                  {widthPercent > 20 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{(day.views || 0).toLocaleString()}</span>}
                                </div>
                                {widthPercent <= 20 && <span style={{ position: "absolute", left: `calc(${Math.max(widthPercent, 3)}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{(day.views || 0).toLocaleString()}</span>}
                              </div>
                              <div style={{ width: "50px", textAlign: "right", fontSize: "11px", color: "#94a3b8" }}>{day.clicks || 0} clicks</div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: "16px", padding: "12px 14px", background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", borderRadius: "10px", border: "1px solid #a7f3d0", display: "flex", justifyContent: "space-between" }}>
                        <div><div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Total Views</div><div style={{ fontSize: "14px", fontWeight: 700, color: "#047857" }}>{performanceData.slice(-7).reduce((sum, d) => sum + (d.views || 0), 0).toLocaleString()}</div></div>
                        <div style={{ textAlign: "right" }}><div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Total Clicks</div><div style={{ fontSize: "14px", fontWeight: 700, color: "#047857" }}>{performanceData.slice(-7).reduce((sum, d) => sum + (d.clicks || 0), 0).toLocaleString()}</div></div>
                      </div>
                    </>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No performance data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="Conversion Funnel" subtitle="Views to conversions" minHeight={320}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* Views */}
                    <div style={{ padding: "16px", background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: "12px", border: "1px solid #93c5fd" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <EyeOutlined style={{ fontSize: "20px", color: "#1d4ed8" }} />
                          <div>
                            <div style={{ fontSize: "11px", color: "#1d4ed8", fontWeight: 500 }}>VIEWS</div>
                            <div style={{ fontSize: "20px", fontWeight: 700, color: "#1e40af" }}>{totals.totalViews.toLocaleString()}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#3b82f6" }}>100%</div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div style={{ textAlign: "center", color: "#94a3b8" }}>
                      <RiseOutlined style={{ transform: "rotate(90deg)", fontSize: "20px" }} />
                    </div>

                    {/* Clicks */}
                    <div style={{ padding: "16px", background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", borderRadius: "12px", border: "1px solid #a7f3d0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <SelectOutlined style={{ fontSize: "20px", color: "#059669" }} />
                          <div>
                            <div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>CLICKS</div>
                            <div style={{ fontSize: "20px", fontWeight: 700, color: "#047857" }}>{totals.totalClicks.toLocaleString()}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#10b981" }}>{(totals.ctr || 0).toFixed(1)}%</div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div style={{ textAlign: "center", color: "#94a3b8" }}>
                      <RiseOutlined style={{ transform: "rotate(90deg)", fontSize: "20px" }} />
                    </div>

                    {/* Conversions */}
                    <div style={{ padding: "16px", background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", borderRadius: "12px", border: "1px solid #c4b5fd" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <AimOutlined style={{ fontSize: "20px", color: "#7c3aed" }} />
                          <div>
                            <div style={{ fontSize: "11px", color: "#7c3aed", fontWeight: 500 }}>CONVERSIONS</div>
                            <div style={{ fontSize: "20px", fontWeight: 700, color: "#6d28d9" }}>{totals.totalConversions.toLocaleString()}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#8b5cf6" }}>{(totals.conversionRate || 0).toFixed(1)}%</div>
                      </div>
                    </div>
                  </div>
                </ChartCard>
              </Col>
            </Row>

            {/* Charts Row 2 */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Promotion Status" subtitle="Active, inactive & scheduled" minHeight={200}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
                    <div style={{
                      padding: "20px 30px",
                      background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                      border: "1px solid #a7f3d0",
                      borderRadius: "14px",
                      textAlign: "center",
                      flex: "1 1 auto",
                      minWidth: "120px",
                      maxWidth: "180px"
                    }}>
                      <CheckCircleOutlined style={{ fontSize: "24px", color: "#10b981", marginBottom: "8px" }} />
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#047857" }}>{totals.activePromotions}</div>
                      <div style={{ fontSize: "12px", color: "#059669", fontWeight: 500 }}>Active</div>
                    </div>
                    <div style={{
                      padding: "20px 30px",
                      background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                      border: "1px solid #cbd5e1",
                      borderRadius: "14px",
                      textAlign: "center",
                      flex: "1 1 auto",
                      minWidth: "120px",
                      maxWidth: "180px"
                    }}>
                      <PauseCircleOutlined style={{ fontSize: "24px", color: "#64748b", marginBottom: "8px" }} />
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#475569" }}>{totals.inactivePromotions}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>Inactive</div>
                    </div>
                    <div style={{
                      padding: "20px 30px",
                      background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      border: "1px solid #fcd34d",
                      borderRadius: "14px",
                      textAlign: "center",
                      flex: "1 1 auto",
                      minWidth: "120px",
                      maxWidth: "180px"
                    }}>
                      <ScheduleOutlined style={{ fontSize: "24px", color: "#d97706", marginBottom: "8px" }} />
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#b45309" }}>{totals.scheduledPromotions}</div>
                      <div style={{ fontSize: "12px", color: "#d97706", fontWeight: 500 }}>Scheduled</div>
                    </div>
                    <div style={{
                      padding: "20px 30px",
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      border: "1px solid #93c5fd",
                      borderRadius: "14px",
                      textAlign: "center",
                      flex: "1 1 auto",
                      minWidth: "120px",
                      maxWidth: "180px"
                    }}>
                      <GiftOutlined style={{ fontSize: "24px", color: "#1d4ed8", marginBottom: "8px" }} />
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#1e40af" }}>{totals.totalPromotions}</div>
                      <div style={{ fontSize: "12px", color: "#1d4ed8", fontWeight: 500 }}>Total</div>
                    </div>
                  </div>
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Gamification Overview" subtitle="Game plays & wins" minHeight={200}>
                  {totals.gamificationStats?.totalPlays > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
                      <div style={{
                        padding: "16px 24px",
                        background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                        border: "1px solid #c4b5fd",
                        borderRadius: "14px",
                        textAlign: "center",
                        flex: "1 1 auto",
                        minWidth: "100px",
                        maxWidth: "160px"
                      }}>
                        <PlayCircleOutlined style={{ fontSize: "20px", color: "#7c3aed", marginBottom: "6px" }} />
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "#6d28d9" }}>{(totals.gamificationStats?.totalPlays || 0).toLocaleString()}</div>
                        <div style={{ fontSize: "11px", color: "#8b5cf6", fontWeight: 500 }}>Total Plays</div>
                      </div>
                      <div style={{
                        padding: "16px 24px",
                        background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                        border: "1px solid #a7f3d0",
                        borderRadius: "14px",
                        textAlign: "center",
                        flex: "1 1 auto",
                        minWidth: "100px",
                        maxWidth: "160px"
                      }}>
                        <TrophyOutlined style={{ fontSize: "20px", color: "#10b981", marginBottom: "6px" }} />
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "#047857" }}>{(totals.gamificationStats?.totalWins || 0).toLocaleString()}</div>
                        <div style={{ fontSize: "11px", color: "#059669", fontWeight: 500 }}>Total Wins</div>
                      </div>
                      <div style={{
                        padding: "16px 24px",
                        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                        border: "1px solid #fcd34d",
                        borderRadius: "14px",
                        textAlign: "center",
                        flex: "1 1 auto",
                        minWidth: "100px",
                        maxWidth: "160px"
                      }}>
                        <PercentageOutlined style={{ fontSize: "20px", color: "#d97706", marginBottom: "6px" }} />
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "#b45309" }}>{(totals.gamificationStats?.winRate || 0).toFixed(1)}%</div>
                        <div style={{ fontSize: "11px", color: "#d97706", fontWeight: 500 }}>Win Rate</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <PlayCircleOutlined style={{ fontSize: "48px", color: "#e2e8f0", marginBottom: "12px" }} />
                      <Text style={{ color: "#64748b", fontWeight: 600, display: "block" }}>No gamification data</Text>
                      <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>Create gamified promotions to see stats</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Audience Distribution */}
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ChartCard title="Audience Distribution" subtitle="Target audience breakdown" minHeight={160}>
                  {totals.audienceBreakdown?.targetAudience?.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
                      {totals.audienceBreakdown.targetAudience.map((audience, index) => {
                        const audienceColors = {
                          all: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", border: "#93c5fd", text: "#1d4ed8" },
                          free: { bg: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", border: "#cbd5e1", text: "#475569" },
                          paid: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "#a7f3d0", text: "#047857" },
                          new: { bg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", border: "#fcd34d", text: "#b45309" },
                          returning: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", border: "#c4b5fd", text: "#6d28d9" },
                        };
                        const colorConfig = audienceColors[audience.audience] || audienceColors.all;
                        return (
                          <div key={audience.audience} style={{
                            padding: "16px 24px",
                            background: colorConfig.bg,
                            border: `1px solid ${colorConfig.border}`,
                            borderRadius: "14px",
                            textAlign: "center",
                            minWidth: "120px",
                            flex: "1 1 auto",
                            maxWidth: "180px"
                          }}>
                            <div style={{ fontSize: "11px", color: colorConfig.text, fontWeight: 500, marginBottom: "4px", textTransform: "capitalize" }}>{audience.audience}</div>
                            <div style={{ fontSize: "24px", fontWeight: 700, color: colorConfig.text }}>{audience.count}</div>
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                              promotions
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <TeamOutlined style={{ fontSize: "48px", color: "#e2e8f0", marginBottom: "12px" }} />
                      <Text style={{ color: "#64748b", fontWeight: 600, display: "block" }}>No audience data available</Text>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "performance" && (
          <>
            {/* Views Over Time */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24}>
                <ChartCard title="Views Over Time" subtitle={`Last ${dateRange} days`} minHeight={350}>
                  {performanceData.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {performanceData.slice(-14).map((day, index) => {
                        const widthPercent = ((day.views || 0) / maxDailyViews) * 100;
                        const date = new Date(day.date);
                        return (
                          <div key={day.date} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "80px", fontSize: "12px", color: "#64748b" }}>{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                            <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: widthPercent > 15 ? "8px" : 0 }}>
                                {widthPercent > 15 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{(day.views || 0).toLocaleString()}</span>}
                              </div>
                              {widthPercent <= 15 && <span style={{ position: "absolute", left: `calc(${widthPercent}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{(day.views || 0).toLocaleString()}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Clicks and Conversions */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Clicks Over Time" subtitle="Daily click trends" minHeight={300}>
                  {performanceData.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {performanceData.slice(-10).map((day) => {
                        const widthPercent = ((day.clicks || 0) / maxDailyClicks) * 100;
                        const date = new Date(day.date);
                        return (
                          <div key={`clicks-${day.date}`} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "60px", fontSize: "12px", color: "#64748b" }}>{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                            <div style={{ flex: 1, height: "20px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${Math.max(widthPercent, 2)}%`, height: "100%", background: "linear-gradient(90deg, #10b981 0%, #059669 100%)", borderRadius: "4px" }} />
                            </div>
                            <div style={{ width: "50px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#10b981" }}>{(day.clicks || 0).toLocaleString()}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Conversions Over Time" subtitle="Daily conversion trends" minHeight={300}>
                  {performanceData.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {performanceData.slice(-10).map((day) => {
                        const widthPercent = ((day.conversions || 0) / Math.max(maxDailyConversions, 1)) * 100;
                        const date = new Date(day.date);
                        return (
                          <div key={`conv-${day.date}`} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "60px", fontSize: "12px", color: "#64748b" }}>{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                            <div style={{ flex: 1, height: "20px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${Math.max(widthPercent, 2)}%`, height: "100%", background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)", borderRadius: "4px" }} />
                            </div>
                            <div style={{ width: "50px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#8b5cf6" }}>{(day.conversions || 0).toLocaleString()}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "gamification" && (
          <>
            {/* Game Type Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {gameTypes.map((game) => {
                const gameStats = totals.gamificationStats?.byType?.find(t => t.type === game.key) || {};
                return (
                  <Col xs={12} sm={6} key={game.key}>
                    <div style={{
                      background: "#fff",
                      borderRadius: "16px",
                      padding: "20px",
                      border: `2px solid ${game.color}20`,
                      textAlign: "center"
                    }}>
                      <div style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: `${game.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px",
                        color: game.color,
                        fontSize: "20px"
                      }}>
                        {game.icon}
                      </div>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: game.color }}>{(gameStats.plays || 0).toLocaleString()}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>{game.label}</div>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>{gameStats.wins || 0} wins</div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Win Rate by Game Type" subtitle="Success percentages" minHeight={300}>
                  {totals.gamificationStats?.byType?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {totals.gamificationStats.byType.map((game, index) => {
                        const gameConfig = gameTypes.find(g => g.key === game.type) || { label: game.type, color: colors[index % colors.length] };
                        const winRate = game.plays > 0 ? ((game.wins / game.plays) * 100) : 0;
                        return (
                          <div key={game.type} style={{
                            padding: "14px 16px",
                            background: `${gameConfig.color}10`,
                            borderRadius: "12px",
                            border: `1px solid ${gameConfig.color}30`
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <Text strong style={{ color: gameConfig.color }}>{gameConfig.label}</Text>
                              <Text style={{ color: gameConfig.color, fontWeight: 700 }}>{winRate.toFixed(1)}%</Text>
                            </div>
                            <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${winRate}%`, height: "100%", background: gameConfig.color, borderRadius: "4px" }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "11px", color: "#64748b" }}>
                              <span>{game.plays} plays</span>
                              <span>{game.wins} wins</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No gamification data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Prize Distribution" subtitle="Prizes won by type" minHeight={300}>
                  {totals.gamificationStats?.prizeDistribution?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.gamificationStats.prizeDistribution.map((prize, index) => {
                        const maxCount = Math.max(...totals.gamificationStats.prizeDistribution.map(p => p.count), 1);
                        const widthPercent = (prize.count / maxCount) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={prize.prize || index} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {index < 3 && <CrownOutlined style={{ color: "#f59e0b" }} />}
                                <Text strong style={{ fontSize: "13px" }}>{prize.prize || "Unknown Prize"}</Text>
                              </div>
                              <Text style={{ fontWeight: 700, color }}>{prize.count}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No prize data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Top Winners */}
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ChartCard title="Top Winners" subtitle="Users with most wins" badge={totals.gamificationStats?.topWinners?.length} minHeight={250}>
                  {totals.gamificationStats?.topWinners?.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "12px" }}>
                      {totals.gamificationStats.topWinners.slice(0, 8).map((winner, index) => (
                        <div key={winner.userId || index} style={{
                          padding: "14px",
                          background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                          border: index < 3 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                          borderRadius: "12px"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                {index < 3 && <TrophyOutlined style={{ color: index === 0 ? "#f59e0b" : index === 1 ? "#94a3b8" : "#cd7f32", fontSize: "14px" }} />}
                                <Text strong style={{ fontSize: "13px" }}>{winner.userName || winner.email || "Anonymous"}</Text>
                              </div>
                              <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                                {winner.plays || 0} plays total
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: "20px", fontWeight: 700, color: "#7c3aed" }}>{winner.wins || 0}</div>
                              <div style={{ fontSize: "10px", color: "#64748b" }}>wins</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No winners data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "audience" && (
          <>
            {/* Audience Summary */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #93c5fd",
                  textAlign: "center"
                }}>
                  <TeamOutlined style={{ fontSize: "24px", color: "#1d4ed8", marginBottom: "8px" }} />
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#1e40af" }}>
                    {totals.audienceBreakdown?.targetAudience?.reduce((sum, a) => sum + a.count, 0) || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#3b82f6", fontWeight: 500 }}>Total Audiences</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #a7f3d0",
                  textAlign: "center"
                }}>
                  <FireOutlined style={{ fontSize: "24px", color: "#10b981", marginBottom: "8px" }} />
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#047857" }}>
                    {totals.audienceBreakdown?.planBreakdown?.length || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#059669", fontWeight: 500 }}>Plan Types</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #c4b5fd",
                  textAlign: "center"
                }}>
                  <CrownOutlined style={{ fontSize: "24px", color: "#7c3aed", marginBottom: "8px" }} />
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#6d28d9" }}>
                    {totals.audienceBreakdown?.userTypeBreakdown?.length || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#8b5cf6", fontWeight: 500 }}>User Types</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                  borderRadius: "16px",
                  padding: "20px",
                  border: "1px solid #fcd34d",
                  textAlign: "center"
                }}>
                  <AimOutlined style={{ fontSize: "24px", color: "#d97706", marginBottom: "8px" }} />
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "#b45309" }}>
                    {totals.activePromotions || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "#d97706", fontWeight: 500 }}>Active Campaigns</div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={8}>
                <ChartCard title="Target Audience" subtitle="Distribution by audience type" minHeight={350}>
                  {totals.audienceBreakdown?.targetAudience?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.audienceBreakdown.targetAudience.map((audience, index) => {
                        const maxCount = Math.max(...totals.audienceBreakdown.targetAudience.map(a => a.count), 1);
                        const widthPercent = (audience.count / maxCount) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={audience.audience} style={{
                            padding: "14px 16px",
                            background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <Text strong style={{ fontSize: "13px", textTransform: "capitalize" }}>
                                {index === 0 && <TrophyOutlined style={{ color: "#f59e0b", marginRight: 4 }} />}
                                {audience.audience}
                              </Text>
                              <Text style={{ fontWeight: 700, color }}>{audience.count}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No audience data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="Plan Breakdown" subtitle="Promotions by target plan" minHeight={350}>
                  {totals.audienceBreakdown?.planBreakdown?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.audienceBreakdown.planBreakdown.map((plan, index) => {
                        const maxCount = Math.max(...totals.audienceBreakdown.planBreakdown.map(p => p.count), 1);
                        const widthPercent = (plan.count / maxCount) * 100;
                        const planColors = {
                          all: "#3b82f6",
                          free: "#64748b",
                          basic: "#10b981",
                          pro: "#8b5cf6",
                          enterprise: "#f59e0b",
                        };
                        const color = planColors[plan.plan?.toLowerCase()] || colors[index % colors.length];
                        return (
                          <div key={plan.plan} style={{
                            padding: "14px 16px",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <Text strong style={{ fontSize: "13px", textTransform: "capitalize" }}>{plan.plan}</Text>
                              <Text style={{ fontWeight: 700, color }}>{plan.count}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plan data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={8}>
                <ChartCard title="User Type Distribution" subtitle="New vs returning users" minHeight={350}>
                  {totals.audienceBreakdown?.userTypeBreakdown?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.audienceBreakdown.userTypeBreakdown.map((userType, index) => {
                        const maxCount = Math.max(...totals.audienceBreakdown.userTypeBreakdown.map(u => u.count), 1);
                        const widthPercent = (userType.count / maxCount) * 100;
                        const typeColors = {
                          new: "#10b981",
                          returning: "#3b82f6",
                          all: "#8b5cf6",
                        };
                        const color = typeColors[userType.type?.toLowerCase()] || colors[index % colors.length];
                        return (
                          <div key={userType.type} style={{
                            padding: "14px 16px",
                            background: index === 0 ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #a7f3d0" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <Text strong style={{ fontSize: "13px", textTransform: "capitalize" }}>{userType.type}</Text>
                              <Text style={{ fontWeight: 700, color }}>{userType.count}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No user type data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "geographic" && (
          <>
            {/* Device Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #93c5fd",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <MobileOutlined style={{ fontSize: "24px", color: "#1d4ed8", marginBottom: "8px" }} />
                  <div style={{ fontSize: "14px", color: "#1d4ed8", fontWeight: 500, marginBottom: "8px" }}>Mobile</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#1e40af" }}>
                    {(totals.deviceBreakdown?.mobile || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(59, 130, 246, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${totals.deviceBreakdown?.mobilePercent || 0}%`, height: "100%", background: "#3b82f6", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#1d4ed8" }}>{totals.deviceBreakdown?.mobilePercent || 0}%</span>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #a7f3d0",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <LaptopOutlined style={{ fontSize: "24px", color: "#059669", marginBottom: "8px" }} />
                  <div style={{ fontSize: "14px", color: "#059669", fontWeight: 500, marginBottom: "8px" }}>Desktop</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#047857" }}>
                    {(totals.deviceBreakdown?.desktop || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(16, 185, 129, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${totals.deviceBreakdown?.desktopPercent || 0}%`, height: "100%", background: "#10b981", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#059669" }}>{totals.deviceBreakdown?.desktopPercent || 0}%</span>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{
                  background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e879f9",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <TabletOutlined style={{ fontSize: "24px", color: "#a855f7", marginBottom: "8px" }} />
                  <div style={{ fontSize: "14px", color: "#a855f7", fontWeight: 500, marginBottom: "8px" }}>Tablet</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: "#7c3aed" }}>
                    {(totals.deviceBreakdown?.tablet || 0).toLocaleString()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: "rgba(139, 92, 246, 0.2)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: `${totals.deviceBreakdown?.tabletPercent || 0}%`, height: "100%", background: "#8b5cf6", borderRadius: "3px" }} />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#a855f7" }}>{totals.deviceBreakdown?.tabletPercent || 0}%</span>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Browser Distribution" subtitle="Views by browser" minHeight={350}>
                  {totals.deviceBreakdown?.browsers?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.deviceBreakdown.browsers.slice(0, 8).map((browser, index) => {
                        const maxViews = Math.max(...totals.deviceBreakdown.browsers.map(b => b.views || b.count), 1);
                        const views = browser.views || browser.count || 0;
                        const widthPercent = (views / maxViews) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={browser.browser || browser.name} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" : "#fff",
                            border: index < 3 ? "1px solid #e2e8f0" : "1px solid #f1f5f9",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: color }} />
                                <Text strong style={{ fontSize: "13px" }}>{browser.browser || browser.name || "Unknown"}</Text>
                              </div>
                              <Text style={{ fontWeight: 700, color }}>{views.toLocaleString()}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No browser data" />
                  )}
                </ChartCard>
              </Col>

              <Col xs={24} lg={12}>
                <ChartCard title="Country Distribution" subtitle="Views by country" badge={totals.countryBreakdown?.length} minHeight={350}>
                  {totals.countryBreakdown?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {totals.countryBreakdown.slice(0, 8).map((country, index) => {
                        const maxViews = Math.max(...totals.countryBreakdown.map(c => c.views || c.count), 1);
                        const views = country.views || country.count || 0;
                        const widthPercent = (views / maxViews) * 100;
                        const color = colors[index % colors.length];
                        return (
                          <div key={country.country || country.code} style={{
                            padding: "12px 14px",
                            background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {index === 0 && <TrophyOutlined style={{ color: "#f59e0b" }} />}
                                <Text strong style={{ fontSize: "13px" }}>{country.country || country.name || "Unknown"}</Text>
                              </div>
                              <Text style={{ fontWeight: 700, color }}>{views.toLocaleString()}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: color, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No country data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {activeTab === "campaigns" && (
          <>
            {/* Type Breakdown */}
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {(totals.typeBreakdown || []).slice(0, 6).map((type, index) => {
                const typeColors = {
                  banner: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", border: "#93c5fd", text: "#1d4ed8" },
                  popup: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", border: "#c4b5fd", text: "#7c3aed" },
                  floating: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "#a7f3d0", text: "#047857" },
                  inline: { bg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", border: "#fcd34d", text: "#b45309" },
                  exit_intent: { bg: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", border: "#f9a8d4", text: "#db2777" },
                  gamified: { bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", border: "#fecaca", text: "#dc2626" },
                };
                const colorConfig = typeColors[type.type] || { bg: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", border: "#cbd5e1", text: "#475569" };
                return (
                  <Col xs={12} sm={8} lg={4} key={type.type}>
                    <div style={{
                      background: colorConfig.bg,
                      borderRadius: "16px",
                      padding: "20px",
                      border: `1px solid ${colorConfig.border}`,
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: colorConfig.text }}>{type.count}</div>
                      <div style={{ fontSize: "12px", color: colorConfig.text, fontWeight: 500, marginTop: 4, textTransform: "capitalize" }}>
                        {type.type?.replace("_", " ")}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Promotions Table */}
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ChartCard title="Individual Promotion Performance" subtitle="All promotions with metrics" badge={topPromotions?.length} minHeight={400}>
                  {topPromotions?.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <Table
                        dataSource={topPromotions}
                        rowKey={(record) => record._id || record.id}
                        pagination={{ pageSize: 10, size: "small" }}
                        size="small"
                        columns={[
                          {
                            title: "Promotion",
                            dataIndex: "name",
                            key: "name",
                            render: (text, record, index) => (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {index < 3 && <TrophyOutlined style={{ color: index === 0 ? "#f59e0b" : index === 1 ? "#94a3b8" : "#cd7f32" }} />}
                                <Text strong ellipsis style={{ maxWidth: 200 }}>{text || record.title}</Text>
                              </div>
                            ),
                          },
                          {
                            title: "Type",
                            dataIndex: "type",
                            key: "type",
                            render: (type) => (
                              <Tag color={type === "gamified" ? "purple" : type === "banner" ? "blue" : type === "popup" ? "green" : "default"} style={{ textTransform: "capitalize" }}>
                                {type?.replace("_", " ")}
                              </Tag>
                            ),
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (status) => (
                              <Tag color={status === "active" ? "success" : status === "scheduled" ? "warning" : "default"}>
                                {status}
                              </Tag>
                            ),
                          },
                          {
                            title: "Views",
                            dataIndex: "views",
                            key: "views",
                            align: "right",
                            render: (views) => <Text style={{ color: "#3b82f6", fontWeight: 600 }}>{(views || 0).toLocaleString()}</Text>,
                          },
                          {
                            title: "Clicks",
                            dataIndex: "clicks",
                            key: "clicks",
                            align: "right",
                            render: (clicks) => <Text style={{ color: "#10b981", fontWeight: 600 }}>{(clicks || 0).toLocaleString()}</Text>,
                          },
                          {
                            title: "Conversions",
                            dataIndex: "conversions",
                            key: "conversions",
                            align: "right",
                            render: (conversions) => <Text style={{ color: "#8b5cf6", fontWeight: 600 }}>{(conversions || 0).toLocaleString()}</Text>,
                          },
                          {
                            title: "CTR",
                            key: "ctr",
                            align: "right",
                            render: (_, record) => {
                              const ctr = record.views > 0 ? ((record.clicks / record.views) * 100).toFixed(1) : "0.0";
                              return <Text style={{ color: "#f59e0b", fontWeight: 600 }}>{ctr}%</Text>;
                            },
                          },
                          {
                            title: "Conv. Rate",
                            key: "convRate",
                            align: "right",
                            render: (_, record) => {
                              const rate = record.clicks > 0 ? ((record.conversions / record.clicks) * 100).toFixed(1) : "0.0";
                              return <Text style={{ color: "#ec4899", fontWeight: 600 }}>{rate}%</Text>;
                            },
                          },
                        ]}
                      />
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No promotions data" />
                  )}
                </ChartCard>
              </Col>
            </Row>

            {/* Status Breakdown */}
            <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
              <Col xs={24}>
                <ChartCard title="Status Breakdown" subtitle="Promotions by status" minHeight={160}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "center" }}>
                    <div style={{
                      padding: "20px 40px",
                      background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                      border: "1px solid #a7f3d0",
                      borderRadius: "14px",
                      textAlign: "center",
                      minWidth: "150px"
                    }}>
                      <CheckCircleOutlined style={{ fontSize: "28px", color: "#10b981", marginBottom: "8px" }} />
                      <div style={{ fontSize: "32px", fontWeight: 700, color: "#047857" }}>{totals.activePromotions}</div>
                      <div style={{ fontSize: "13px", color: "#059669", fontWeight: 500 }}>Active</div>
                    </div>
                    <div style={{
                      padding: "20px 40px",
                      background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                      border: "1px solid #cbd5e1",
                      borderRadius: "14px",
                      textAlign: "center",
                      minWidth: "150px"
                    }}>
                      <PauseCircleOutlined style={{ fontSize: "28px", color: "#64748b", marginBottom: "8px" }} />
                      <div style={{ fontSize: "32px", fontWeight: 700, color: "#475569" }}>{totals.inactivePromotions}</div>
                      <div style={{ fontSize: "13px", color: "#64748b", fontWeight: 500 }}>Inactive</div>
                    </div>
                    <div style={{
                      padding: "20px 40px",
                      background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      border: "1px solid #fcd34d",
                      borderRadius: "14px",
                      textAlign: "center",
                      minWidth: "150px"
                    }}>
                      <ClockCircleOutlined style={{ fontSize: "28px", color: "#d97706", marginBottom: "8px" }} />
                      <div style={{ fontSize: "32px", fontWeight: 700, color: "#b45309" }}>{totals.scheduledPromotions}</div>
                      <div style={{ fontSize: "13px", color: "#d97706", fontWeight: 500 }}>Scheduled</div>
                    </div>
                  </div>
                </ChartCard>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default PromotionsDashboard;
